import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames/bind';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import { observer } from 'mobx-react';
import { observable, computed, reaction, action } from 'mobx';
import { EditorState } from 'draft-js';
import ReactDataSheet from './DatasheetEditor';
import TableEditorStore from './store';
import styles from './table.module.scss';
import PlusIcon from '../../../../shared/icons/Plus';
import DeleteIcon from '@material-ui/icons/Delete';
import atomicBlock from '@studionand/ada-next/src/legacy/atomicBlock';
import withStores from '@studionand/ada-next/src/stores/withStores';

const cx = classnames.bind(styles);

const stopEventPropagation = e => {
  e.preventDefault();
  e.stopPropagation();
  return false;
};

const cellIsSelected = ({
  col,
  row,
  rowSelectionRange,
  columnSelectionRange,
}) => {
  const selectedByRow =
    rowSelectionRange.length === 2 &&
    row >= rowSelectionRange[0] &&
    row <= rowSelectionRange[1];
  const selectedByColumn =
    columnSelectionRange.length === 2 &&
    col >= columnSelectionRange[0] &&
    col <= columnSelectionRange[1];
  return selectedByRow || selectedByColumn;
};

const AddButton = ({ visible, store, ...props }) => (
  <div
    {...props}
    onMouseOver={store.showInsertionMarker}
    onMouseOut={store.hideInsertionMarker}
    className={cx('add-button', { visible })}
  >
    <PlusIcon />
  </div>
);

const DeleteCellButton = observer(
  ({ store, selectionBoundingBox, scrollOffset }) => {
    const { hasFullRowSelected, hasFullColumnSelected } = store;
    const isEnabled = hasFullRowSelected || hasFullColumnSelected;
    if (!isEnabled || !selectionBoundingBox) return null;
    const { top, right } = selectionBoundingBox;

    return (
      <Button
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          transform: `translate(${right + 20}px, ${top + scrollOffset.y}px)`,
        }}
        // className={cx('delete-cell-button')}
        onClick={isEnabled ? () => store.removeSelectedCells() : null}
      >
        <DeleteIcon />
      </Button>
    );
  },
);

const RowInsertionMarker = observer(({ row, type, store }) => {
  const isRendered = (row === 0 && type === 'before') || type === 'after';
  const isVisible =
    store.insertionMarkerIsVisible &&
    row === store.insertRowIndex &&
    type === store.insertRowType;
  if (!isRendered) return null;
  return (
    <div
      className={cx('insertion-marker', 'row', type, { active: isVisible })}
    />
  );
});

const ColumnInsertionMarker = observer(({ col, type, store }) => {
  const isRendered = type === 'after' || (col === 0 && type === 'before');
  const isVisible =
    store.insertionMarkerIsVisible &&
    col === store.insertColumnIndex &&
    type === store.insertColumnType;
  if (!isRendered) return null;
  return (
    <div
      className={cx('insertion-marker', 'column', type, {
        active: isVisible,
      })}
    />
  );
});

const RowInteractionArea = observer(({ row, col, type, store }) => {
  const { insertRowIndex, insertRowType, setInsertRowIndex, insertRow } = store;
  const isRendered =
    (row === 0 && col === 0) || (col === 0 && type === 'after');
  if (!isRendered) return null;
  return (
    <div
      onMouseOver={() => setInsertRowIndex(row, type)}
      onMouseLeave={() => setInsertRowIndex(null, null)}
      onMouseDown={stopEventPropagation}
      className={cx('row-interaction-area', type)}
    >
      <AddButton
        store={store}
        onClick={e => {
          insertRow();
          stopEventPropagation(e);
        }}
        visible={insertRowIndex === row && type === insertRowType}
      />
    </div>
  );
});

const ColumnInteractionArea = observer(({ row, col, type, store }) => {
  const {
    insertColumnIndex,
    insertColumnType,
    setInsertColumnIndex,
    insertColumn,
  } = store;
  const isRendered =
    (row === 0 && col === 0) || (row === 0 && type === 'after');
  if (!isRendered) return null;
  return (
    <div
      onMouseOver={() => setInsertColumnIndex(col, type)}
      onMouseOut={() => setInsertColumnIndex(null, null)}
      onMouseDown={stopEventPropagation}
      className={cx('column-interaction-area', type)}
    >
      <AddButton
        store={store}
        onClick={e => {
          insertColumn();
          stopEventPropagation(e);
        }}
        visible={insertColumnIndex === col && type === insertColumnType}
      />
    </div>
  );
});

function findDataEntity(contentBlock, contentState) {
  let dataEntity, entityKey;
  contentBlock.findEntityRanges(character => {
    entityKey = character.getEntity();
    if (
      entityKey !== null &&
      (contentState.getEntity(entityKey).getType() === 'table-editor' ||
        contentState.getEntity(entityKey).getType() === 'price-chart')
    ) {
      dataEntity = contentState.getEntity(entityKey);
    }
    return false;
  });
  return [dataEntity, entityKey];
}

@withStores('pagesStore', 'appStore')
@observer
class TableEditor extends Component {
  static propTypes = {
    data: PropTypes.array,
    config: PropTypes.object,
    // required for saving in ada
    blockProps: PropTypes.object.isRequired,
    isInteractive: PropTypes.bool.isRequired,
    block: PropTypes.any.isRequired,
    contentState: PropTypes.any.isRequired,
    // readOnly: PropTypes.bool.isRequired,
    // editorState: PropTypes.object.isRequired,
    // setEditorState: PropTypes.func.isRequired,
    // onToggleActive: PropTypes.func.isRequired,
  };

  static defaultProps = {
    data: [[]],
    config: null,
    blockProps: null,
    dataRenderComponent: null,
  };

  constructor(props) {
    super(props);
    const { data: defaultData, identifier: blockIdentifier } = props.blockProps;
    let blockData;
    const [dataEntity] = findDataEntity(props.block, props.contentState);
    const initialBlockProps = dataEntity ? dataEntity.getData() : {};
    blockData =
      (initialBlockProps.data && JSON.parse(initialBlockProps.data)) || null;
    this.cellReferences = {};
    // this is the data of the table
    const initialData = blockData || defaultData;
    this.store = new TableEditorStore(initialData);
    // window.store = this.store;
    // always update the editorstate if store.data changes
    this.applyChangesToEditorState = reaction(
      () => this.store.serializedData,
      newSerializedContent => {
        const { block, contentState, blockProps } = this.props;
        const { editorState } = blockProps;
        if (!block || !contentState || !editorState) return;        
        const [entity, entityKey] = findDataEntity(props.block, props.contentState);
        if (!entity || block.type !== blockIdentifier) return;
        // const entity = contentState.getEntity(entityKey);
        const previousProps = entity.getData();
        if (newSerializedContent === previousProps.data) return;
        const serializedProps = { data: newSerializedContent };
        const newProps = { ...previousProps, ...serializedProps };
        this.props.pagesStore.currentPage.updateEntityData(
          block.getKey(),
          entityKey,
          newProps,
        );
      },
    );

    // this code ensures that we always have a working selection
    this.reactOnSelectionChange = reaction(
      () => this.store.selection,
      () => {
        this.selectionBoundingBox = this.computeSelectionBB();
      },
    );
  }

  componentDidMount() {
    window.addEventListener('scroll', this.handleScroll);
  }

  // update the store if we receive data from the block
  componentWillReceiveProps(nextProps) {
    const { block, contentState, blockProps } = nextProps;
    const { identifier: blockIdentifier } = blockProps;
    const entityKey = block.getEntityAt(0);
    // skip update if there is no data, or blocktype is not correct
    if (!entityKey || block.type !== blockIdentifier) return;
    const entity = contentState.getEntity(entityKey);
    const nextBlockProps = entity.getData();
    // skip update if there is no data or no change
    if (
      !nextBlockProps.data ||
      nextBlockProps.data === this.store.serializedData
    )
      return;
    const newData = JSON.parse(nextBlockProps.data);
    this.store.setData(newData);
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
  }

  setCellReference = ({ row, col, ref }) => {
    this.cellReferences[`${row}-${col}`] = ref;
  };

  removeCellReference = ({ row, col }) => {
    delete this.cellReferences[`${row}-${col}`];
  };

  // this little helper function goes through all <td>-refs and looks if they have a data-selected attr,
  // if yes, add them to the bounding box and return it
  computeSelectionBB() {
    const initalBB = {
      left: 10000,
      top: 10000,
      right: 0,
      bottom: 0,
    };
    return Object.values(this.cellReferences)
      .filter(f => f && f.hasAttribute('data-selected'))
      .reduce((p, c) => {
        const {
          left,
          top,
          right,
          bottom,
          // x,
          // y,
        } = c.getBoundingClientRect();
        const { min, max } = Math;
        return {
          left: min(left, p.left),
          top: min(top, p.top),
          right: max(right, p.right),
          bottom: max(bottom, p.bottom),
        };
      }, initalBB);
  }

  @observable scrollOffset = { y: 0, x: 0 };
  @observable selectionBoundingBox = null;

  @action.bound
  handleScroll() {
    this.selectionBoundingBox = this.computeSelectionBB();
  }

  @computed
  get editor() {
    // todo: optimize render flow to have per-cell-updates
    // just to be on the safe side, let's convert our observable 2d-array into a normal js-array
    // this is a possible place for great optimizations, since now even if only one element inside the array changes, everything gets re-rendered
    const data = this.store.data.map(r => r.map(c => ({ ...c })));
    return (
      <ReactDataSheet
        className={cx('table-editor')}
        selection={this.store.selection}
        data={data}
        dataRenderer={cell => cell.value}
        valueRenderer={cell => cell.value}
        attributesRenderer={cell => ({ value: cell.value })}
        cellRenderer={this.cellEditor}
        onRef={this.setCellReference}
        onRefRemove={this.removeCellReference}
        onChange={this.store.applyChange}
        onSelectionChange={this.store.applySelection}
        // onFocus={event => console.log(event)}
        // onBlur={event => console.log(event)}
      />
    );
  }

  @computed
  get dataRenderer() {
    const data = this.store.data.map(r => r.map(c => ({ ...c })));
    if (!this.props.blockProps) return null;
    return <this.props.blockProps.dataRenderComponent data={data} />;
  }

  // this renders the actual cell (interactions and content), it gets ALL props
  cellEditor = observer(({ row, col, children }) => {
    const cellProps = { row, col };
    const { rowSelectionRange, columnSelectionRange } = this.store;
    const isSelected = cellIsSelected({
      ...cellProps,
      rowSelectionRange,
      columnSelectionRange,
    });
    return (
      <div className={cx('editor-cell', { selected: isSelected })}>
        <RowInsertionMarker {...cellProps} store={this.store} type="before" />
        <RowInsertionMarker {...cellProps} store={this.store} type="after" />
        <ColumnInsertionMarker
          {...cellProps}
          store={this.store}
          type="before"
        />
        <ColumnInsertionMarker {...cellProps} store={this.store} type="after" />
        <RowInteractionArea {...cellProps} store={this.store} type="before" />
        <RowInteractionArea {...cellProps} store={this.store} type="after" />
        <ColumnInteractionArea
          {...cellProps}
          store={this.store}
          type="before"
        />
        <ColumnInteractionArea {...cellProps} store={this.store} type="after" />
        {children}
      </div>
    );
  });

  get entityKey() {
    return this.props.block.getEntityAt(0);
  }

  get entity() {
    return (
      (this.entityKey &&
        this.props.blockProps.editorState
          .getCurrentContent()
          .getEntity(this.entityKey)) ||
      null
    );
  }

  render() {
    const { isInteractive, appStore } = this.props;
    return (
      <Box
        pt={8}
        pb={4}
        pr={0}
        pl={0}
        className={cx('table-editor-wrap', { active: isInteractive })}
      >
        {appStore.editMode && isInteractive ? this.editor : this.dataRenderer}
        <DeleteCellButton
          store={this.store}
          selectionBoundingBox={this.selectionBoundingBox}
          scrollOffset={this.scrollOffset}
        />
      </Box>
    );
  }
}

export default atomicBlock({ alignment: false })(TableEditor);
//  AtomicBlockInteractions(TableEditor, {
//   showMenu: true,
//   alignable: false,
//   deletable: true,
//   deletableWithBackspace: false,
// });
